import { useState, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { FieldErrors, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '../../../../app/store/configureStore';
import { useTranslation } from 'react-i18next';
import { useRestaurant } from '../../../../app/hooks/useRestaurant';
import FormButtons from '../../../../app/components/FormButtons';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { createMealItemCustomizationAsync } from './createMealItemCustomizationSlice';
import {
  fetchMealItemCustomizationAsync,
  updateMealItemCustomizationAsync,
} from './mealItemCustomizationSlice';
import {
  MealItemCustomizationFormData,
  toMealItemCustomizationFormData,
} from '../../../../app/data/models/mealItems/mealItemCustomizationFormData';
import LoadingComponent from '../../../../app/layout/LoadingComponent';
import ErrorScreen from '../../../../app/components/ErrorScreen';
import {
  LanguageCode,
  LocalizedLanguageFormData,
} from '../../../../app/data/models/common/localizedLanguage';
import { mealItemCustomizationValidationSchema } from './mealItemCustomizationValidation';
import ListTitleBox from '../../../../app/components/list/ListTitleBox';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import ErrorComponent from '../../../../app/layout/ErrorComponent';
import { LanguageFormTabs } from '../../../../app/components/LanguageFormTabs';

const MealItemCustomizationForm = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { restaurantId, customizationId } = useParams();
  const [isLoading, setIsLoading] = useState(!!customizationId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customization, setCustomization] =
    useState<MealItemCustomizationFormData | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);

  const {
    restaurant,
    isLoading: isRestaurantLoading,
    error: restaurantError,
    retry: retryRestaurant,
  } = useRestaurant(restaurantId);
  const languageCodes = restaurant?.languageCodes || [];

  // Declare header at the very top so it's available for all usages
  const handleBack = () => {
    navigate(`/manager/restaurants/${restaurantId}/meal-item-customizations`);
  };

  // Only include internalNotes in defaultValues if needed
  const defaultValues: MealItemCustomizationFormData = {
    customerInstructions: languageCodes.reduce(
      (acc, code) => ({ ...acc, [code]: '' }),
      {} as LocalizedLanguageFormData
    ),
  };

  const { control, handleSubmit, reset } =
    useForm<MealItemCustomizationFormData>({
      mode: 'onTouched',
      resolver: yupResolver(
        mealItemCustomizationValidationSchema(
          t,
          languageCodes as LanguageCode[]
        )
      ) as unknown as Resolver<MealItemCustomizationFormData>,
      defaultValues,
    });

  // Reset form when restaurant/languageCodes change (to update defaultValues)
  useEffect(() => {
    if (restaurant && languageCodes.length > 0 && !customizationId) {
      reset({
        customerInstructions: languageCodes.reduce(
          (acc, code) => ({ ...acc, [code]: '' }),
          {} as LocalizedLanguageFormData
        ),
      });
    }
  }, [restaurant, languageCodes, customizationId, reset]);

  const header = (
    <ListTitleBox
      title={
        customization
          ? t('meal-item-customization.form.edit.title')
          : t('meal-item-customization.form.create.title')
      }
      onBackClick={handleBack}
    />
  );

  useEffect(() => {
    if (customizationId && !customization) {
      fetchCustomization();
    }
  }, [customizationId]);

  useEffect(() => {
    if (customization) {
      const resetData: MealItemCustomizationFormData = {
        customerInstructions: customization.customerInstructions,
      };
      if (customization.internalNotes) {
        resetData.internalNotes = customization.internalNotes;
      }
      reset(resetData);
    }
  }, [customization, reset]);

  const fetchCustomization = async () => {
    if (!customizationId) return;

    try {
      setIsLoading(true);
      const response = await dispatch(
        fetchMealItemCustomizationAsync({
          restaurantId,
          customizationId,
        })
      ).unwrap();
      setCustomization(toMealItemCustomizationFormData(response));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: MealItemCustomizationFormData) => {
    console.log('Submitting customization form:', data);
    try {
      setIsSubmitting(true);
      if (customizationId) {
        await dispatch(
          updateMealItemCustomizationAsync({
            restaurantId,
            customizationId,
            data,
          })
        ).unwrap();
        toast.success(t('meal-item-customization.update.success'));
      } else {
        await dispatch(
          createMealItemCustomizationAsync({ restaurantId, data })
        ).unwrap();
        toast.success(t('meal-item-customization.create.success'));
      }
    } catch (_error: unknown) {
      if (_error instanceof AxiosError) {
        toast.error(_error.response?.data?.title || t('common.error'));
      } else if (_error instanceof Error) {
        toast.error(_error.message || t('common.error'));
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      handleBack();
      setIsSubmitting(false);
    }
  };

  const onError = (errors: FieldErrors<MealItemCustomizationFormData>) => {
    console.error('Form errors:', errors);
    if (!languageCodes.length) return;

    // Check for errors in customerInstructions
    if (errors.customerInstructions) {
      const errorLanguageIndex = languageCodes.findIndex(
        (code) =>
          errors.customerInstructions && errors.customerInstructions[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
        return;
      }
    }

    // Check for errors in internalNotes
    if (errors.internalNotes) {
      const errorLanguageIndex = languageCodes.findIndex(
        (code) => errors.internalNotes && errors.internalNotes[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
      }
    }
  };

  const fields = [
    {
      name: 'customerInstructions',
      label: t('meal-item-customization.form.fields.instructions'),
      multiline: true,
      rows: 3,
    },
    {
      name: 'internalNotes',
      label: t('meal-item-customization.form.fields.notes'),
      multiline: true,
      rows: 3,
    },
  ];

  if (isLoading || isRestaurantLoading) {
    return <LoadingComponent header={header} message={t('common.loading')} />;
  }

  if (restaurantError || !restaurant)
    return <ErrorComponent onPrimaryAction={retryRestaurant} header={header} />;

  if (customizationId && !customization) {
    return (
      <>
        {header}
        <ErrorScreen
          title={t('meal-item-customization.error.load-failed')}
          description={t('meal-item-customization.error.load-description')}
          primaryButtonText={t('common.try-again')}
          onPrimaryAction={fetchCustomization}
        />
      </>
    );
  }

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <LanguageFormTabs<MealItemCustomizationFormData>
            languageCodes={languageCodes}
            control={control}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            fields={fields}
          />
          <FormButtons
            loading={isSubmitting}
            submitText={customization ? t('common.save') : t('common.create')}
          />
        </form>
      </Box>
    </>
  );
};

export default MealItemCustomizationForm;
